import { Injectable, NgZone } from '@angular/core';

import {
  collectHelpAnchors,
  HelpAnchorInfo,
  IAnchorRegistry,
  waitForElement,
  WaitForAnchorOptions,
} from '@ups/xplat/base/in-app-help';

/**
 * CSS selector matching any of a step's candidate anchor paths, each one exactly
 * or as a trailing `/`-segment run — the two halves of the page-root stamping
 * race described on `anchorPathVariants`.
 *
 * Exported so the selector shape stays unit tested; the DOM query itself is
 * verified on the live serve.
 */
export function helpPathSelector(pathVariants: string[]): string {
  return pathVariants
    .flatMap((v) => [`[help-path="${v}"]`, `[help-path$="/${v}"]`])
    .join(', ');
}

/**
 * The web anchor registry: an anchor is a DOM element carrying `help-path`, so
 * resolution is a query and "wait for it" is a MutationObserver, both already
 * built in `waitForElement`.
 */
@Injectable({ providedIn: 'root' })
export class WebAnchorRegistryService implements IAnchorRegistry<HTMLElement> {
  constructor(private readonly ngZone: NgZone) {}

  resolve(pathVariants: string[]): HTMLElement | null {
    return document.querySelector<HTMLElement>(helpPathSelector(pathVariants));
  }

  waitFor(
    pathVariants: string[],
    options: WaitForAnchorOptions = {}
  ): Promise<HTMLElement | null> {
    // ngZone: the observer must watch a document-wide render burst (a nav step
    // lazy-loads a whole page) WITHOUT scheduling change detection per mutation.
    // waitForElement re-enters the zone on resolution.
    return waitForElement(helpPathSelector(pathVariants), {
      ngZone: this.ngZone,
      timeoutMs: options.timeoutMs,
    });
  }

  inventory(): HelpAnchorInfo[] {
    return collectHelpAnchors();
  }

  inferTapToAdvance(anchor: HTMLElement): boolean {
    return anchor.tagName === 'BUTTON';
  }

  bindTap(anchor: HTMLElement, handler: () => void): () => void {
    anchor.addEventListener('click', handler);
    return () => anchor.removeEventListener('click', handler);
  }
}
